import type { Permission } from '@/api/permissions';
import type { CatalogGroup, CatalogPermission } from '@/shared/modules/role-board/types';

export type CoverageLevel = 'full' | 'partial' | 'none';

export type GroupCoverage = {
  granted: number;
  total: number;
  level: CoverageLevel;
};

export type ComparedRole = {
  id: string;
  permissions: ReadonlySet<Permission>;
};

export type ComparisonPermissionRow = {
  permission: CatalogPermission;
  /** One entry per compared role, in the order the roles were given. */
  granted: boolean[];
};

export type ComparisonGroupRow = {
  group: CatalogGroup;
  cells: GroupCoverage[];
  permissions: ComparisonPermissionRow[];
};

export const groupCoverage = (
  group: CatalogGroup,
  permissions: ReadonlySet<Permission>,
): GroupCoverage => {
  const total = group.permissions.length;
  const granted = group.permissions.filter(({ permission }) => permissions.has(permission)).length;
  const level: CoverageLevel = granted === 0 ? 'none' : granted === total ? 'full' : 'partial';

  return { granted, total, level };
};

const allEqual = (values: readonly boolean[]) => values.every((value) => value === values[0]);

/**
 * The role comparison matrix: catalogue groups as rows, roles as columns. With
 * `onlyDifferences`, a permission row survives only when the roles disagree on it, and a group
 * survives only when it keeps at least one such row.
 */
export const buildRoleComparison = (
  groups: readonly CatalogGroup[],
  roles: readonly ComparedRole[],
  { onlyDifferences }: { onlyDifferences: boolean },
): ComparisonGroupRow[] =>
  groups.flatMap((group) => {
    const permissions = group.permissions
      .map((permission) => ({
        permission,
        granted: roles.map((role) => role.permissions.has(permission.permission)),
      }))
      .filter((row) => !onlyDifferences || !allEqual(row.granted));

    if (!permissions.length) return [];

    return [
      { group, cells: roles.map((role) => groupCoverage(group, role.permissions)), permissions },
    ];
  });
